import React, { useState, useMemo, useEffect } from 'react';
import { Filter, ChevronDown as ChevronDownIcon, ChevronRight, Plus, Pencil, Send, Trash2, Loader, CheckCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useProjects } from '../../contexts/ProjectsContext';
import { useReports } from '../../contexts/ReportsContext';
import { OvertimeForm } from './OvertimeForm';
import { OvertimeMobileCard } from './OvertimeMobileCard';
import { OvertimeEditForm } from './OvertimeEditForm';

// Проверяем, можно ли ещё редактировать/удалять отчёт:
// доступно до 0:00 (конец суток) того дня, когда он был создан (submittedAt)
// Кнопки доступны пока отчёт не отправлен в таблицу (submitted_early = false).
// Время не проверяем на фронте — блокировку по времени делает сервер при попытке редактирования.
const isEditable = (report) => !report.submittedEarly;

// Кнопки для строки десктопной таблицы
const OvertimeRowActions = ({ report, darkMode }) => {
  const { deleteOvertimeReport, submitOvertimeEarly } = useReports();
  const [showEditForm, setShowEditForm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [submittingEarly, setSubmittingEarly] = useState(false);

  const canEdit = isEditable(report);
  const isSubmittedEarly = report.submittedEarly;

  const handleDelete = async () => {
    if (!window.confirm('Удалить этот отчёт? Это действие необратимо.')) return;
    setDeleting(true);
    const result = await deleteOvertimeReport(report.id);
    setDeleting(false);
    if (!result.success) alert(`Ошибка удаления: ${result.error}`);
  };

  const handleSubmitEarly = async () => {
    if (!window.confirm('Отправить отчёт в таблицу досрочно? После этого редактирование и удаление будут недоступны.')) return;
    setSubmittingEarly(true);
    const result = await submitOvertimeEarly(report.id);
    setSubmittingEarly(false);
    if (!result.success) alert(`Ошибка: ${result.error}`);
  };

  return (
    <>
      <div className="flex items-center gap-1.5">
        {isSubmittedEarly ? (
          <span className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
            darkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700'
          }`}>
            <CheckCircle className="w-3 h-3" />
            В таблице
          </span>
        ) : canEdit ? (
          <>
            <button
              onClick={() => setShowEditForm(true)}
              title="Редактировать"
              className={`p-1.5 rounded-lg transition-colors ${
                darkMode
                  ? 'text-blue-400 hover:bg-blue-900/40'
                  : 'text-blue-600 hover:bg-blue-50'
              }`}
            >
              <Pencil className="w-4 h-4" />
            </button>

            <button
              onClick={handleSubmitEarly}
              disabled={submittingEarly}
              title="Отправить досрочно"
              className={`p-1.5 rounded-lg transition-colors ${
                darkMode
                  ? 'text-green-400 hover:bg-green-900/40'
                  : 'text-green-600 hover:bg-green-50'
              } ${submittingEarly ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {submittingEarly
                ? <Loader className="w-4 h-4 animate-spin" />
                : <Send className="w-4 h-4" />
              }
            </button>

            <button
              onClick={handleDelete}
              disabled={deleting}
              title="Удалить"
              className={`p-1.5 rounded-lg transition-colors ${
                darkMode
                  ? 'text-red-400 hover:bg-red-900/40'
                  : 'text-red-600 hover:bg-red-50'
              } ${deleting ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {deleting
                ? <Loader className="w-4 h-4 animate-spin" />
                : <Trash2 className="w-4 h-4" />
              }
            </button>
          </>
        ) : (
          <span className={`text-xs italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            —
          </span>
        )}
      </div>

      {showEditForm && (
        <OvertimeEditForm report={report} onClose={() => setShowEditForm(false)} />
      )}
    </>
  );
};

export const OvertimeView = () => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const { projects } = useProjects();
  const {
    submittedReports,
    overtimeFilters, setOvertimeFilters,
    expandedOvertimeMonths,
    toggleOvertimeMonth,
    filterOvertimeReports,
    groupReportsByMonth,
    calculateMonthOvertimeTotal,
  } = useReports();

  const [showForm, setShowForm] = useState(false);

  const userReports = useMemo(() => {
    if (!currentUser) return [];
    return submittedReports.filter(r => {
      if (r.type !== 'overtime') return false;
      if (r.user_id && currentUser.id && r.user_id === currentUser.id) return true;
      if (r.userName && currentUser.name && r.userName === currentUser.name) return true;
      if (r.username && currentUser.username && r.username === currentUser.username) return true;
      return false;
    });
  }, [submittedReports, currentUser]);

  useEffect(() => {
    console.log(`Переработки: отчетов для пользователя: ${userReports.length}`);
  }, [userReports.length]);

  const filteredReports = filterOvertimeReports(userReports);
  const groupedByMonth = groupReportsByMonth(filteredReports);

  return (
    <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-md overflow-hidden mb-6`}>
      <div className={`px-4 md:px-6 py-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <h2 className={`text-lg md:text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          Отчеты о переработках
        </h2>
        <p className={`text-xs md:text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Учет переработок и работы в выходные дни
        </p>
      </div>

      <div className="p-4 md:p-6 space-y-4">
        <button
          onClick={() => setShowForm(true)}
          className="w-full touch-target bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center space-x-2"
        >
          <Plus className="w-5 h-5" />
          <span>Добавить отчет о переработке</span>
        </button>

        {/* Фильтры */}
        <div className={`p-3 md:p-4 rounded-lg ${
          darkMode ? 'bg-gray-700/50 border border-gray-600' : 'bg-gray-50 border border-gray-200'
        }`}>
          <h4 className="font-medium mb-3 flex items-center text-sm md:text-base">
            <Filter className="w-4 h-4 mr-2" />
            Фильтры
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className={`block text-xs md:text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Проект
              </label>
              <select
                value={overtimeFilters.project}
                onChange={(e) => setOvertimeFilters({...overtimeFilters, project: e.target.value})}
                className={`w-full px-3 py-2 md:py-2.5 text-sm rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'
                }`}
              >
                <option value="">Все проекты</option>
                {projects.map(project => (
                  <option key={project} value={project}>{project}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={`block text-xs md:text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Вид работы
              </label>
              <select
                value={overtimeFilters.workType}
                onChange={(e) => setOvertimeFilters({...overtimeFilters, workType: e.target.value})}
                className={`w-full px-3 py-2 md:py-2.5 text-sm rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'
                }`}
              >
                <option value="">Все виды</option>
                <option value="Переработка">Переработка</option>
                <option value="Переработка дорога">Переработка дорога</option>
                <option value="Выезд в выходной день">Выезд в выходной день</option>
                <option value="Работа в выходной день">Работа в выходной день</option>
                <option value="Возврат в выходной день">Возврат в выходной день</option>
              </select>
            </div>
            <div>
              <label className={`block text-xs md:text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Дата с
              </label>
              <input
                type="date"
                value={overtimeFilters.dateFrom}
                onChange={(e) => setOvertimeFilters({...overtimeFilters, dateFrom: e.target.value})}
                className={`w-full px-3 py-2 md:py-2.5 text-sm rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'
                }`}
              />
            </div>
            <div>
              <label className={`block text-xs md:text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Дата по
              </label>
              <input
                type="date"
                value={overtimeFilters.dateTo}
                onChange={(e) => setOvertimeFilters({...overtimeFilters, dateTo: e.target.value})}
                className={`w-full px-3 py-2 md:py-2.5 text-sm rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'
                }`}
              />
            </div>
          </div>
        </div>

        {/* Список отчетов */}
        {userReports.length > 0 ? (
          <div className="mt-6">
            <h3 className={`font-bold text-base md:text-lg mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              Ваши переработки
            </h3>
            {Object.entries(groupedByMonth)
              .sort(([a], [b]) => new Date(b) - new Date(a))
              .map(([month, reports]) => (
                <div key={month} className={`mb-4 border rounded-lg ${
                  darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
                }`}>
                  <button
                    onClick={() => toggleOvertimeMonth(month)}
                    className={`w-full px-4 py-3 md:py-4 text-left flex justify-between items-center font-bold ${
                      darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-sm md:text-base">{month}</span>
                    <div className="flex items-center space-x-3">
                      <span className={`px-2 py-1 rounded text-xs ${
                        darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {reports.length} отч.
                      </span>
                      <span className={`px-2 py-1 rounded text-xs font-bold ${
                        darkMode ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-800'
                      }`}>
                        {calculateMonthOvertimeTotal(reports)} ч.
                      </span>
                      {expandedOvertimeMonths?.includes(month) ? (
                        <ChevronDownIcon className="w-4 h-4 md:w-5 md:h-5" />
                      ) : (
                        <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
                      )}
                    </div>
                  </button>

                  {expandedOvertimeMonths?.includes(month) && (
                    <div className="p-3 md:p-4 border-t border-gray-200 dark:border-gray-700">

                      {/* Десктопная таблица */}
                      <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                          <colgroup>
                            <col style={{ width: '9%' }} />
                            <col style={{ width: '13%' }} />
                            <col style={{ width: '28%' }} />
                            <col style={{ width: '24%' }} />
                            <col style={{ width: '9%' }} />
                            <col style={{ width: '17%' }} />
                          </colgroup>
                          <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-semibold">Дата</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold">Время / Часы</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold">Проект</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold">Вид работы</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold">Перераб.</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold">Действия</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reports.map(report => (
                              <tr key={report.id} className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
                                <td className="px-3 py-2 text-xs whitespace-nowrap">
                                  {new Date(report.date).toLocaleDateString('ru-RU')}
                                </td>
                                <td className="px-3 py-2 text-xs">
                                  <div className="whitespace-nowrap">{report.startTime} – {report.endTime}</div>
                                  <div className={`mt-0.5 font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {report.totalHours} ч.
                                  </div>
                                </td>
                                <td className="px-3 py-2 text-xs">
                                  <span
                                    className="block truncate"
                                    title={report.project}
                                  >
                                    {report.project}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-xs">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium leading-tight text-center ${ 
                                    report.workType?.includes('выходной') || report.workType?.includes('Возврат')
                                      ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300'
                                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300'
                                  }`}>
                                    {report.workType || 'Не указан'}
                                  </span>
                                </td>
                                <td className={`px-3 py-2 text-xs font-bold whitespace-nowrap ${
                                  report.overtimeHours > 0
                                    ? 'text-red-600 dark:text-red-400'
                                    : 'text-gray-500 dark:text-gray-400'
                                }`}>
                                  {report.overtimeHours} ч.
                                </td>
                                <td className="px-3 py-2">
                                  <OvertimeRowActions report={report} darkMode={darkMode} />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Мобильные карточки */}
                      <div className="md:hidden space-y-2">
                        {reports.map(report => (
                          <OvertimeMobileCard key={report.id} report={report} darkMode={darkMode} />
                        ))}
                      </div>

                    </div>
                  )}
                </div>
              ))}
          </div>
        ) : (
          <div className="text-center py-8 md:py-12">
            <p className={`text-base md:text-lg ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              У вас пока нет отправленных отчетов о переработках
            </p>
          </div>
        )}
      </div>

      {showForm && <OvertimeForm onClose={() => setShowForm(false)} />}
    </div>
  );
};

export default OvertimeView;
